import type { RecordingCapture } from "./session-provenance";
import type { TrackingSport } from "@/lib/tracking/types";

export type RecordingStatus = {
  mode: "checking" | "consent" | "setup" | "live" | "finishing" | "save-failed" | "story" | "recovery" | "recovery-error";
  sessionId: string | null;
  offlineOwner?: string | null;
  capture?: RecordingCapture;
  sport: TrackingSport;
  paused: boolean;
  elapsedS: number;
  distanceKm: number;
};

export function retainRecording(status: RecordingStatus | null): boolean {
  return status !== null && ["live", "finishing", "save-failed", "recovery", "recovery-error"].includes(status.mode);
}

/** An active recorder keeps its launch parameters when another page changes the URL. */
export function recordingReturnHref(search: string): string {
  const source = new URLSearchParams(search);
  const query = new URLSearchParams();
  for (const key of ["base", "outingId", "outing", "demo", "native-feed", "fall-feed", "offline-pack", "install-demo"]) {
    const value = source.get(key);
    if (value && value.length <= 1024) query.set(key, value);
  }
  return `/companion/track${query.size ? `?${query}` : ""}`;
}

export function recordingLabel(status: RecordingStatus): string {
  if (status.mode === "finishing") return "Saving your session";
  if (status.mode === "save-failed") return "Session needs saving";
  if (status.mode === "recovery" || status.mode === "recovery-error") return "Recording needs review";
  return status.paused ? "Recording paused" : "Recording your session";
}

/** Web Locks are released by the browser on tab/process death, without stale timers. */
export function acquireRecordingLock(manager: LockManager): Promise<(() => void) | null> {
  return new Promise((resolve, reject) => {
    void manager.request("peakwave-active-recording", { mode: "exclusive", ifAvailable: true }, lock => {
      if (!lock) { resolve(null); return; }
      return new Promise<void>(release => resolve(release));
    }).catch(reject);
  });
}
